import { useCallback, useEffect, useRef, useState } from 'react';

/** Re-render every `ms` so live timers animate between server state pushes. */
export function useTick(ms = 500): void {
  const [, set] = useState(0);
  useEffect(() => {
    const id = setInterval(() => set((n) => (n + 1) % 1_000_000), ms);
    return () => clearInterval(id);
  }, [ms]);
}

/** ms until a counting-DOWN mm:ss (ceil, see formatCountdown) next flips a digit:
 *  the value ⌈left/1000⌉ decrements when `left` reaches (⌈left/1000⌉−1)·1000.
 *  Exported for the unit pin; only meaningful for left > 0. */
export function msToNextSecondBoundary(left: number): number {
  return left - (Math.ceil(left / 1000) - 1) * 1000; // ∈ (0, 1000]
}

const CLOCK_RESYNC_MS = 30_000;

/** Sync ONCE and HOLD. The server offset is essentially constant for a session
 *  (both clocks advance in real time), so re-measuring it every push only injects
 *  THAT push's one-way latency jitter — which made the countdown JUMP: a laggy
 *  sample shoved it backward, and "correcting" to a better sample later shoved it
 *  FORWARD, skipping seconds (Jake's 8→6). Hold the first sample steady; only a
 *  large discontinuity (a real device-clock change / long resume) resyncs. */
export function nextServerOffset(current: number | null, candidate: number): number {
  return current === null || Math.abs(candidate - current) > CLOCK_RESYNC_MS ? candidate : current;
}
const ALIGN_EPS_MS = 20; // fire just AFTER the boundary so the crossed value is read
const IDLE_TICK_MS = 1000; // no live deadline → keep now()-derived displays fresh

/**
 * A "server now" clock that advances smoothly between pushes. `now()` =
 * Date.now() + offset keeps ticking from the LOCAL clock, so timers move between
 * broadcasts instead of freezing at the last serverNow.
 *
 * Two properties this hook guarantees, both from Jake's field report:
 *
 * 1. NEVER JUMPS. The offset (serverNow − localNow) is measured ONCE (first push)
 *    and HELD. Re-measuring it every push (the old behaviour) re-sampled that
 *    push's one-way latency, so the countdown jerked — backward on a laggy push,
 *    and forward when a later push "corrected" it, skipping seconds. The offset is
 *    essentially constant for a session, so holding it makes `now()` advance
 *    smoothly off the local clock. Only a large discontinuity (a real device-clock
 *    change / long resume) resyncs.
 *
 * 2. UPDATES EXACTLY ON THE SECOND. Rather than a fixed poll (which lands the
 *    digit up to a tick late), it self-schedules a re-render at each `deadline`'s
 *    next second boundary, so mm:ss flips the instant one more second is gone —
 *    "0:01" appears with exactly one second left. Pass every deadline the caller
 *    renders; with none it idles at ~1 Hz for elapsed/staleness displays.
 */
export function useServerClock(
  serverNow: number,
  deadlines: ReadonlyArray<number | null | undefined> = [],
): () => number {
  const offset = useRef<number | null>(null);
  const synced = useRef<number>(NaN);
  if (serverNow !== synced.current) {
    synced.current = serverNow;
    offset.current = nextServerOffset(offset.current, serverNow - Date.now());
  }
  const now = useCallback(() => Date.now() + (offset.current ?? 0), []);

  const [, bump] = useState(0);
  // Re-arm only when the set of live deadlines (or the synced push) actually
  // changes; the self-scheduling chain below survives the bump re-renders.
  const key = deadlines
    .filter((d): d is number => typeof d === 'number')
    .sort((a, b) => a - b)
    .join('|');
  useEffect(() => {
    let id: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      const t = now();
      let wait = IDLE_TICK_MS;
      for (const d of deadlines) {
        if (typeof d !== 'number') continue;
        const left = d - t;
        if (left <= 0) continue;
        wait = Math.min(wait, msToNextSecondBoundary(left));
      }
      id = setTimeout(
        () => {
          bump((n) => (n + 1) % 1_000_000);
          schedule();
        },
        Math.max(0, wait) + ALIGN_EPS_MS,
      );
    };
    schedule();
    return () => {
      if (id) clearTimeout(id);
    };
    // `deadlines` is captured via `key`; `now` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, serverNow, now]);

  return now;
}
